import {
    PsHttpService,
    type PatSnapCommonResponseData,
} from '@patsnap/tahiti-core/common/ps-http';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';

/**
 * 智能体设置API接口定义
 */
export interface AgentSettingsApi {
    /**
     * 获取智能体设置
     */
    getAgentSettings(): Promise<AgentSettingsData>;

    /**
     * 更新智能体设置
     */
    updateAgentSettings(data: UpdateAgentSettingsRequest): Promise<boolean>;

    /**
     * 检查CES权限
     */
    checkCesPermission(): Promise<boolean>;
}

/**
 * 智能体设置数据
 */
export interface AgentSettingsData {
    /** 是否开启个性化响应 */
    open_setting: boolean;
    /** 用户画像 */
    profile: string;
}

/**
 * 更新智能体设置请求参数
 */
export interface UpdateAgentSettingsRequest {
    /** 是否开启个性化响应 */
    open_setting: boolean;
    /** 用户画像 */
    profile: string;
}

export const AgentSettingsApi = Symbol('AgentSettingsApi');

/**
 * 智能体设置API实现
 */
@injectable()
export class AgentSettingsApiImpl extends PsHttpService implements AgentSettingsApi {
    get options() {
        return {
            baseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}`,
            headers: {
                'X-API-Version': '1.0',
            },
        };
    }

    /**
     * 获取智能体设置
     */
    getAgentSettings(): Promise<AgentSettingsData> {
        return this.get<PatSnapCommonResponseData<AgentSettingsData>>({
            url: '/eureka/ces-setting',
            method: 'GET',
        }).then((res) => {
            const data = res?.data;
            // 如果没有数据，返回默认值
            return data || {
                open_setting: false,
                profile: '',
            };
        }).catch(() => {
            // API调用失败时，返回默认值
            return {
                open_setting: false,
                profile: '',
            };
        }) as Promise<AgentSettingsData>;
    }

    /**
     * 更新智能体设置
     */
    updateAgentSettings(data: UpdateAgentSettingsRequest): Promise<boolean> {
        return this.post<PatSnapCommonResponseData<boolean>>({
            url: '/eureka/update-ces-setting',
            method: 'POST',
            data,
        }).then((res) => {
            return res?.status as unknown as boolean;
        }) as Promise<boolean>;
    }

    /**
     * 检查CES权限
     */
    checkCesPermission(): Promise<boolean> {
        return this.get<PatSnapCommonResponseData<boolean>>({
            url: '/eureka/ces/permission',
            method: 'GET',
        }).then((res) => {
            return res?.data ?? false;
        }) as Promise<boolean>;
    }
}
